import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { BUILD_ID } from "../buildInfo";
import {
  UserRound, Globe, LogOut, ChevronRight, Bell, Settings, ShieldCheck, PlayCircle, Fingerprint, Sparkles, Type, Image as ImageIcon, BookOpen, Languages, Building2, Zap, Trash2, Pencil, ChevronUp, ChevronDown, Plus, Bot,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { playVoice } from "../lib/sounds";
import { buddyEnabled } from "../lib/buddyPrefs";
import { BuddyMemoriesCard } from "../components/BuddyMemoriesCard";
import { BuddyRemindersCard } from "../components/BuddyRemindersCard";
import { OfflineSettingsCard } from "../components/OfflineSettingsCard";
import { showWelcomeTour } from "../components/WelcomeTour";
import { getAppLock, setAppLock, getFontSize, setFontSize, mediaSrcById, api, BACKEND_URL } from "../api";
import { resizeToJpeg } from "../lib/imageResize";
import { PdfViewer } from "../components/PdfViewer";
import { LiveUpdateStatus } from "../components/LiveUpdateStatus";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const BUDDY_VOICE_IDS = ["echo", "alloy", "fable", "onyx", "nova", "shimmer"];

/** Personal quick phrases (⚡): each user manages their OWN list here.
 *  PM1 never sees PM2's phrases; the MASTER sees everyone's (owner badge).
 *  Company-wide phrases are managed by the Admin in the Admin panel. */
function PhrasesDialog({ open, onClose }) {
  const { t, user } = useApp();
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null); // {id, text}
  const [busy, setBusy] = useState(false);
  const isMaster = !!user?.super_admin;

  const load = () => api.get("/my/quick-replies").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => { if (open) { setText(""); setEditing(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await api.post("/my/quick-replies", { text: v });
      toast.success(t("quick_reply_added"));
      setText("");
      load();
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const v = (editing?.text || "").trim();
    if (!v) return;
    try {
      await api.patch(`/my/quick-replies/${editing.id}`, { text: v });
      toast.success(t("quick_reply_saved"));
      setEditing(null);
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/my/quick-replies/${id}`);
      toast.success(t("quick_reply_deleted"));
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  // Reorder: swap adjacent items — only within the SAME owner's list
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    if (a.user_id !== b.user_id) return;
    try {
      await Promise.all([
        api.patch(`/my/quick-replies/${a.id}`, { order: b.order ?? j + 1 }),
        api.patch(`/my/quick-replies/${b.id}`, { order: a.order ?? idx + 1 }),
      ]);
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="my-phrases-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> {t("phrases_row")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">{t("phrases_desc")}</p>
        <div className="flex gap-2">
          <Input
            data-testid="my-phrase-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder={t("quick_reply_placeholder")}
            maxLength={200}
          />
          <Button data-testid="my-phrase-add-btn" onClick={add} disabled={!text.trim() || busy} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" /> {t("quick_reply_add")}
          </Button>
        </div>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {list === null && [1, 2, 3].map((i) => <div key={i} className="h-11 bg-muted rounded-xl animate-pulse" />)}
          {list !== null && list.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">{t("quick_replies_empty")}</p>
          )}
          {(list || []).map((p, idx) => (
            <div key={p.id} data-testid={`my-phrase-item-${p.id}`} className="rounded-xl border bg-card px-3 py-2.5 flex items-center gap-2">
              {editing?.id === p.id ? (
                <>
                  <Input
                    data-testid="my-phrase-edit-input"
                    value={editing.text}
                    onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
                    maxLength={200}
                    autoFocus
                    className="h-8"
                  />
                  <Button size="sm" className="h-8 px-2.5 shrink-0" data-testid="my-phrase-save-btn" onClick={saveEdit}>{t("save")}</Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 shrink-0" onClick={() => setEditing(null)}>{t("cancel")}</Button>
                </>
              ) : (
                <>
                  <div className="flex flex-col shrink-0">
                    <button type="button" aria-label="up" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === 0 || list[idx - 1]?.user_id !== p.user_id} onClick={() => move(idx, -1)}>
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" aria-label="down" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === list.length - 1 || list[idx + 1]?.user_id !== p.user_id} onClick={() => move(idx, 1)}>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <span className="text-sm min-w-0 flex-1 break-words">{p.text}</span>
                  {isMaster && !p.own && (
                    <Badge variant="outline" className="text-[9px] px-1.5 shrink-0">{p.owner_name}</Badge>
                  )}
                  <button type="button" data-testid={`my-phrase-edit-btn-${p.id}`} title={t("edit")} className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0" onClick={() => setEditing({ id: p.id, text: p.text })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" data-testid={`my-phrase-delete-btn-${p.id}`} title={t("delete")} className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0" onClick={() => remove(p.id)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MorePage() {
  const { user, logout, t, lang } = useApp();
  const navigate = useNavigate();
  const [phrasesOpen, setPhrasesOpen] = useState(false); // personal quick phrases (⚡)
  // App manual (PDF) — opens in the in-app viewer, in the user's language and ROLE TIER:
  // leader (installer) < supervisor < admin — each tier only shows its own features
  const [manualOpen, setManualOpen] = useState(false);
  const manualTier = user.role === "admin"
    ? "admin"
    : ["supervisor", "manager", "office"].includes(user.role) ? "supervisor" : "leader";
  const manualLang = lang === "pt" || lang === "es" ? lang : "en";
  const manualMedia = {
    url: `${BACKEND_URL}/manual-onsitecomms-${manualLang}-${manualTier}.pdf`,
    filename: lang === "pt" ? "Manual-OnSiteComms.pdf" : lang === "es" ? "Manual-OnSiteComms-ES.pdf" : "OnSiteComms-Manual.pdf",
    content_type: "application/pdf",
  };
  // Dedicated Buddy & Live Interpreter manual (same for every role)
  const [buddyManualOpen, setBuddyManualOpen] = useState(false);
  const buddyManualMedia = {
    url: `${BACKEND_URL}/manual-buddy-${manualLang}.pdf`,
    filename: lang === "pt" ? "Manual-Buddy-Interprete.pdf" : lang === "es" ? "Manual-Buddy-Interprete-ES.pdf" : "Buddy-Interpreter-Manual.pdf",
    content_type: "application/pdf",
  };

  // My company (small logo + name) shown in the mini profile header
  const [myCompany, setMyCompany] = useState(null);
  useEffect(() => {
    api.get("/company/mine").then((r) => setMyCompany(r.data.company)).catch(() => {});
  }, [user?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = [
    { key: "profile", label: t("profile"), icon: UserRound, action: () => navigate("/profile") },
    { key: "summary", label: t("daily_summary"), icon: Sparkles, action: () => navigate("/summary") },
    { key: "phrases", label: t("phrases_row"), icon: Zap, action: () => setPhrasesOpen(true) },
    { key: "settings", label: t("settings"), icon: Settings, action: () => navigate("/settings") },
    { key: "manual", label: t("app_manual"), icon: BookOpen, action: () => setManualOpen(true) },
    { key: "buddy-manual", label: t("buddy_manual"), icon: Languages, action: () => setBuddyManualOpen(true) },
    { key: "tour", label: t("tour_row"), icon: PlayCircle, action: () => showWelcomeTour() },
    ...(user.role === "admin" ? [{ key: "admin", label: t("admin_panel"), icon: ShieldCheck, action: () => navigate("/admin") }] : []),
    { key: "logout", label: t("sign_out"), icon: LogOut, action: () => { logout(); navigate("/"); } },
  ];

  return (
    <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-6">
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">{t("more")}</h1>

      {/* Mini profile header -> profile page */}
      <Card
        data-testid="more-profile-header"
        onClick={() => navigate("/profile")}
        className="p-4 mb-4 cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3"
      >
        <Avatar className="h-12 w-12">
          {user.avatar_media_id && (
            <AvatarImage src={mediaSrcById(user.avatar_media_id)} alt="" className="object-cover" />
          )}
          <AvatarFallback style={{ backgroundColor: user.avatar_color || "#64748b", color: "#fff" }} className="text-sm font-semibold">
            {initials(user.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{user.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user.email || user.title}</div>
          {/* Company + position */}
          <div className="flex items-center gap-1.5 mt-1 min-w-0" data-testid="more-company-row">
            {myCompany?.logo_base64 ? (
              <img
                src={`data:image/jpeg;base64,${myCompany.logo_base64}`}
                alt=""
                className="h-4 w-4 rounded-[4px] object-contain bg-white border shrink-0"
                data-testid="more-company-logo"
              />
            ) : (
              <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
            )}
            <span className="text-[11px] font-medium truncate" data-testid="more-company-name">
              {myCompany?.name || t("no_company")}
            </span>
            <span className="text-[11px] text-muted-foreground truncate shrink-0" data-testid="more-position">
              · {user.title || t(user.role)}
            </span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Card>

      <div className="space-y-2 mb-8">
        {rows.map((r) => (
          <Card key={r.key} data-testid={`more-row-${r.key}`} onClick={r.action} className="p-4 cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3">
            <r.icon className={`h-5 w-5 ${r.key === "logout" ? "text-destructive" : "text-primary"}`} />
            <span className="text-sm font-medium flex-1">{r.label}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Card>
        ))}
      </div>

      <p className="text-center text-[11px] text-muted-foreground mb-2" data-testid="more-build-id">
        OnSiteComms · Build {BUILD_ID}
      </p>
      <LiveUpdateStatus />

      <PhrasesDialog open={phrasesOpen} onClose={() => setPhrasesOpen(false)} />
      {manualOpen && (
        <PdfViewer open={manualOpen} media={manualMedia} onClose={() => setManualOpen(false)} />
      )}
      {buddyManualOpen && (
        <PdfViewer open={buddyManualOpen} media={buddyManualMedia} onClose={() => setBuddyManualOpen(false)} />
      )}
    </div>
  );
}
